import {
  dateAndTimeToTimezoneStrCurrentZone,
  ZonedDateTimeFactory,
  ZonedDateTimeFormatter,
} from '@csp/csp-common-date-util';
import {
  CspError,
  CspErrorType,
  Maybe,
  SiteId,
  StateAssert,
  UserId,
  VisitLocationType,
  VisitRefsType,
  VisitStatusType,
} from '@csp/csp-common-model';
import { RequestRef } from '@csp/csp-common-scheduling';
import { AppointmentInV1, AppointmentLocationV1, LocationTypeV1 } from '@csp/dmdp-api-appointment-dto';
import { CustomStatusInV1 } from '@csp/dmdp-api-common-dto';
import { isDefined } from '@csp/csp-common-util';
import { VisitConfirmationStatusType } from '../model/VisitConfirmationStatusType';
import { VisitIn } from '../model/VisitIn';
import { VisitCustomStatusType } from '../model/VisitCustomStatusType';

const ZOOM = 'zoom';
const ZOOM_V2 = 'V2';

const toAppointmentLocationV1 = ({
  location,
  ref,
  videoMeetingId,
}: {
  location: VisitLocationType;
  ref?: string;
  videoMeetingId?: string;
}): AppointmentLocationV1 => {
  switch (location) {
    case VisitLocationType.VIDEO:
      return { type: LocationTypeV1.VIDEO, video: { version: ZOOM_V2, vendor: ZOOM, meetingId: videoMeetingId } };
    case VisitLocationType.LOCATION:
      StateAssert.notNull(ref, 'locationType ref must be set');
      return { type: LocationTypeV1.LOCATION, ref };
    case VisitLocationType.PHONE:
      return {
        type: LocationTypeV1.PHONE,
      };
    case VisitLocationType.HOME_VISIT:
      return {
        type: LocationTypeV1.HOME_VISIT,
      };
    default:
      throw new CspError(
        CspErrorType.BAD_STATE,
        `Visit config mapping not implemented for format of type: ${location}`,
      );
  }
};

const toDefaultCustomStatusesInV1 = (): Array<CustomStatusInV1> => [
  {
    type: VisitCustomStatusType.CONFIRMATION,
    value: VisitConfirmationStatusType.NOT_CONFIRMED,
    localTimestamp: ZonedDateTimeFormatter.toZonedDateTimeString(ZonedDateTimeFactory.now()),
  },
];

const toAppointmentInV1 = (
  userId: UserId,
  { date, time, locationType, siteLocation, visitType, description, name, kind }: VisitIn,
  videoMeetingId: Maybe<string>,
  requestRef: RequestRef,
  siteId: Maybe<SiteId>,
): AppointmentInV1 => ({
  title: [name, kind].filter(isDefined).join(' • '),
  scheduleRequestRef: RequestRef.toGenericScheduleRequestRefV1(requestRef),
  type: visitType,
  status: VisitStatusType.BOOKED,
  customStatuses: toDefaultCustomStatusesInV1(),
  participants: [{ userId }],
  startTimestamp: dateAndTimeToTimezoneStrCurrentZone(date, time) || '',
  location: toAppointmentLocationV1({ location: locationType, ref: siteLocation, videoMeetingId }),
  description,
  refs: siteId
    ? [
        {
          key: VisitRefsType.SITE_ORG_ID,
          value: siteId,
        },
      ]
    : undefined,
});

export const VisitMapper = {
  toAppointmentInV1,
};
